import Link from 'next/link'
import { useRouter } from 'next/router'
import { type FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { linkCSS } from 'styles/common'

import { DESKTOP_NAV_LINKS } from '../constants'

import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import isEqual from 'lodash/isEqual'
import { useFetchStakeMigrateData } from 'state/futures/hooks'
import { selectIsolatedPositionsCount } from 'state/futures/isolatedMargin/selectors'
import { useAppSelector } from 'state/hooks'
import {
	selectEscrowedKwentaBalance,
	selectStakedKwentaBalance,
	selectTotalVestable,
} from 'state/staking/selectors'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import { isWeiEqual } from 'utils/wei'
import NavDropdown from './NavDropdown'

const Nav: FC = memo(() => {
	const { t } = useTranslation()
	const { asPath } = useRouter()

	useFetchStakeMigrateData()
	const isolatedPositionsCount = useAppSelector(selectIsolatedPositionsCount, isEqual)
	const stakedKwentaBalance = useAppSelector(selectStakedKwentaBalance, isWeiEqual)
	const escrowedBalance = useAppSelector(selectEscrowedKwentaBalance, isWeiEqual)
	const totalVestable = useAppSelector(selectTotalVestable, isWeiEqual)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const hideStaking = useMemo(() => {
		return stakedKwentaBalance.add(escrowedBalance).add(totalVestable).eq(ZERO_WEI)
	}, [stakedKwentaBalance, escrowedBalance, totalVestable])

	const hideSwaps = !!abstractionWalletEnabled

	const activeLink = useMemo(() => {
		return DESKTOP_NAV_LINKS.find((item) => item.link.includes(asPath.split('/')[1]))?.link
	}, [asPath])

	return (
		<nav>
			<MenuLinks>
				{DESKTOP_NAV_LINKS.filter(
					(l) =>
						!l.hidden &&
						(!l.link.includes('/staking') || !hideStaking) &&
						(!l.link.includes('/swaps') || !hideSwaps)
				).map(({ i18nLabel, dropdown, link }) => {
					if (dropdown && isolatedPositionsCount[PerpsProvider.SNX_V2_OP] > 0) {
						return <NavDropdown key={link} i18nLabel={i18nLabel} {...dropdown} />
					}

					return (
						<Link key={link} href={link}>
							<MenuInside $isActive={link === activeLink}>{t(i18nLabel)}</MenuInside>
						</Link>
					)
				})}
			</MenuLinks>
		</nav>
	)
})

const MenuLinks = styled.ul`
	display: flex;
	align-items: center;
	gap: 25px;
`

const MenuInside = styled.div<{ $isActive: boolean; $isDropDown?: boolean }>`
	${linkCSS};
	padding: ${(props) => (props.$isDropDown ? '4px 9px' : '0px')};
	font-weight: 400;
	font-size: 14px;
	text-transform: capitalize;
	text-align: center;
	border-radius: 100px;
	background: transparent;
	cursor: pointer;
	width: 100%;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	opacity: ${(props) => (props.$isActive ? 1 : 0.5)};
	&:hover {
		opacity: 1;
	}
	transition: all 0.2s ease-in-out;
`

export default Nav
